<script lang="ts">
	import { Img } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { ConversionType } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { BUTTON_COLOR_DEFAULTS, BUTTON_DEFAULT, BUTTON_ICON_CLEARANCE_CLASS, BUTTON_ICON_SIZE_CLASS, DEFAULT_BUTTON_COMPONENT_TEXT } from "#lib/components/flow/editor/appearance/constants.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import { booleanValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { SizeType } from "flowbite-svelte";
	import type { Writable } from "svelte/store";

	let { id, style, page, mode, connectable = false, connectionRequired = false }: ComponentProps = $props();

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const config = $derived(structValue($page.config, id));

	const disabled = $derived(booleanValue(config, "disabled") ?? false);
	const hidden = $derived(booleanValue(config, "hidden") ?? false);
	const backgroundColor = $derived(stringValue(config, "backgroundColor"));
	const borderColor = $derived(stringValue(config, "borderColor"));
	const textColor = $derived(stringValue(config, "textColor"));
	const buttonSize = $derived(stringValue(config, "buttonSize") ?? BUTTON_DEFAULT.buttonSize);
	const borderRadius = $derived(stringValue(config, "borderRadius") ?? BUTTON_DEFAULT.borderRadius);
	const textAlignment = $derived(stringValue(config, "textAlignment") ?? BUTTON_DEFAULT.textAlignment);
	const fontWeight = $derived(stringValue(config, "fontWeight") ?? BUTTON_DEFAULT.fontWeight);
	const imageLeftStruct = $derived(structValue(config, "imageLeft"));
	const imageLeftUrl = $derived(imageLeftStruct ? stringValue(imageLeftStruct, "url") : undefined);
	const imageRightStruct = $derived(structValue(config, "imageRight"));
	const imageRightUrl = $derived(imageRightStruct ? stringValue(imageRightStruct, "url") : undefined);
	const iconLeftStruct = $derived(structValue(config, "iconLeft"));
	const iconLeftIconSet = $derived(iconLeftStruct ? (stringValue(iconLeftStruct, "iconSet") ?? stringValue(iconLeftStruct, "iconset")) : undefined);
	const iconLeftName = $derived(iconLeftStruct ? stringValue(iconLeftStruct, "name") : undefined);
	const iconRightStruct = $derived(structValue(config, "iconRight"));
	const iconRightIconSet = $derived(iconRightStruct ? (stringValue(iconRightStruct, "iconSet") ?? stringValue(iconRightStruct, "iconset")) : undefined);
	const iconRightName = $derived(iconRightStruct ? stringValue(iconRightStruct, "name") : undefined);
	const iconColor = $derived(stringValue(config, "iconColor") ?? BUTTON_COLOR_DEFAULTS.iconColor);
	const resolvedSize = $derived.by(() => (buttonSize ? (buttonSize as SizeType) : undefined));
	const iconSizeClass = $derived(BUTTON_ICON_SIZE_CLASS[buttonSize ?? "md"] ?? BUTTON_ICON_SIZE_CLASS.md);
	const shouldRenderLeftIcon = $derived(Boolean(iconLeftStruct) || Boolean(imageLeftUrl));
	const shouldRenderRightIcon = $derived(connectable && (Boolean(iconRightStruct) || Boolean(imageRightUrl)));
	const iconClearanceClass = $derived(BUTTON_ICON_CLEARANCE_CLASS[buttonSize ?? "md"] ?? BUTTON_ICON_CLEARANCE_CLASS.md);

	// Centered text needs matching insets on both sides whenever either icon is present;
	// asymmetric clearance shifts the optical center. Left/right align only clears real icons.
	const textClearanceClass = $derived.by(() => {
		const clearance = iconClearanceClass;
		if (textAlignment === "center" && (shouldRenderLeftIcon || shouldRenderRightIcon)) {
			return twMerge(clearance.left, clearance.right);
		}
		return twMerge(shouldRenderLeftIcon ? clearance.left : "", shouldRenderRightIcon ? clearance.right : "");
	});

	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");

	async function handleClick(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		await execute(ConversionType.NONE, id, id);
	}

	async function handleKeyDown(e: KeyboardEvent) {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			await execute(ConversionType.NONE, id, id);
		}
	}

	function getThemeOverride(key: string) {
		return stringValue($page.themeOverrides, key) ?? BUTTON_COLOR_DEFAULTS[key];
	}

	const buttonAppearanceStyle = $derived(() => {
		const resolvedBorderColor = borderColor ?? BUTTON_COLOR_DEFAULTS.borderColor;
		const styleMap: Record<string, string | undefined> = {
			"text-align": textAlignment,
			"font-weight": fontWeight,
			"background-color": backgroundColor ?? getThemeOverride("primaryButtonBackgroundColor"),
			"border-color": resolvedBorderColor,
			color: textColor ?? getThemeOverride("primaryButtonTextColor"),
			"border-radius": borderRadius,
			...(resolvedBorderColor ? { "border-width": "1px", "border-style": "solid" } : {}),
		};

		return Object.entries(styleMap)
			.filter(([, v]) => v)
			.map(([k, v]) => `${k}: ${v};`)
			.join(" ");
	});

	const resolvedStyle = $derived.by(() => [style, buttonAppearanceStyle()].filter(Boolean).join(" "));
	const editingClass = $derived(mode === Mode.EDITING ? "pointer-events-none cursor-default" : "");
	const configText = $derived(stringValue(config, "text"));
	const isEditing = $derived(mode && mode === Mode.EDITING);
	const resolvedFallback = $derived(isEditing ? DEFAULT_BUTTON_COMPONENT_TEXT : "");
	const showTokenizedPreview = $derived(isEditing && $tokenProvider?.hasSubstitutionToken(configText));
	const resolvedEditorDisplayText = $derived(configText !== undefined && configText !== "" ? configText : resolvedFallback);
</script>

<div class="relative flex w-full flex-row justify-center" class:hidden={mode !== Mode.EDITING && hidden}>
	{#if mode === Mode.EDITING && hidden}
		<div class="absolute -top-1 -right-1 z-99 flex h-7 w-7 items-center justify-center rounded-full border bg-white/90 text-gray-900">
			<Icon iconSet="heroicons" name="eye-slash" class=" h-4 w-4" />
		</div>
	{/if}
	<Button
		size={resolvedSize}
		data-action-name="landing-page-button"
		data-flow-button
		data-linkable-id={connectable ? id : undefined}
		data-connection-required={connectable ? String(connectionRequired) : undefined}
		class={twMerge("relative flex items-center px-4", stringValue(config, "class") ?? "", editingClass)}
		style={resolvedStyle}
		onclick={mode === Mode.EDITING ? undefined : handleClick}
		onkeydown={mode === Mode.EDITING ? undefined : handleKeyDown}
		{disabled}
		type="button"
	>
		{#if shouldRenderLeftIcon}
			<span class={twMerge("absolute top-1/2 left-4 -translate-y-1/2", iconSizeClass)}>
				{#if imageLeftUrl}
					<Img src={imageLeftUrl} alt="" class={twMerge("mr-2 object-contain", iconSizeClass)} />
				{:else if iconLeftStruct}
					<Icon iconSet={iconLeftIconSet} name={iconLeftName} class={twMerge("mr-2", iconSizeClass)} color={iconColor} />
				{/if}
			</span>
		{/if}
		<div class={twMerge("w-full min-w-0 text-sm", textClearanceClass)}>
			{#if showTokenizedPreview}
				<TokenizedText provider={$tokenProvider} text={configText} {mode} />
			{:else}
				{resolvedEditorDisplayText}
			{/if}
		</div>
		{#if shouldRenderRightIcon}
			<span class={twMerge("absolute top-1/2 right-4 -translate-y-1/2", iconSizeClass)}>
				{#if imageRightUrl}
					<Img src={imageRightUrl} alt="" class={twMerge("ml-2 object-contain", iconSizeClass)} />
				{:else if iconRightStruct}
					<Icon iconSet={iconRightIconSet} name={iconRightName} class={twMerge("ml-2", iconSizeClass)} color={iconColor} />
				{/if}
			</span>
		{/if}
	</Button>
</div>
